/**
 * Pure patch builders for Test-Fit Studio geometry edits.
 *
 * Every builder returns a `patchLayout` payload that stamps
 * `schemaVersion: 2` — the gate that switches the solver onto studio
 * semantics (pin+pack, additive road corridors, custom components) while
 * legacy scenario inputs replay byte-identical. Keeping these pure keeps the
 * replay surface auditable: the studio UI never mutates geometry directly.
 */
import type {
  GenerationLayoutComponentPolygon,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import {
  latLngForLocalAxes,
  localAxesForPoint,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import type {
  GenerationCustomComponent,
  GenerationLayoutConfiguration,
  GenerationLayoutManualPlacement,
} from '../../../../types/generationImpact';

type LayoutPatch = Partial<GenerationLayoutConfiguration>;

/** Drags smaller than this commit nothing (jitter dead-band, metres). */
export const DRAG_DEAD_BAND_M = 0.05;

/** Sub-millimetre translations are treated as no-ops (float-noise guard). */
const TRANSLATION_EPSILON_M = 1e-6;

export function upsertManualPlacement(
  layout: GenerationLayoutConfiguration,
  placement: GenerationLayoutManualPlacement,
): LayoutPatch {
  const existing = layout.manualPlacements ?? [];
  return {
    schemaVersion: 2,
    manualPlacements: [
      ...existing.filter(current => current.componentId !== placement.componentId),
      placement,
    ],
  };
}

export function removeManualPlacement(
  layout: GenerationLayoutConfiguration,
  componentId: string,
): LayoutPatch {
  return {
    schemaVersion: 2,
    manualPlacements: (layout.manualPlacements ?? [])
      .filter(current => current.componentId !== componentId),
  };
}

/**
 * Rename a solver-generated component (block, cooling, switchyard, road…).
 *
 * The name lives in the FROZEN layout (`componentLabels`), so it is part of the
 * saved scenario and replays with it — and because the test-fit builder applies
 * it as a post-pass, the new name propagates to the canvas, layer tree,
 * inspector, exports, takeoffs and reports from this one write.
 *
 * A blank name REMOVES the override (restoring the solver's own label) rather
 * than storing an empty string, so the map never accumulates dead entries.
 */
export function renameComponent(
  layout: GenerationLayoutConfiguration,
  componentId: string,
  label: string,
): LayoutPatch {
  const next = { ...(layout.componentLabels ?? {}) };
  const trimmed = label.trim();
  if (trimmed) next[componentId] = trimmed;
  else delete next[componentId];
  return { schemaVersion: 2, componentLabels: next };
}

/** The fields a bulk pin needs from a solved component rectangle. */
export interface BulkPinnableRectangle {
  id: string;
  kind: string;
  xM: number;
  yM: number;
  rotationDeg?: number;
}

/**
 * Freeze the whole layout: pin every packer-placed component exactly where it
 * currently sits. This is the "manual" end of the placement axis — with every
 * component pinned the packer has nothing left to arrange.
 *
 * Excluded by construction: custom components (they already carry their own
 * position and are not packer output) and access roads (derived geometry that
 * is re-cut from the components it serves). Returns null when there is nothing
 * left to pin, so the caller can skip an empty history entry.
 */
export function pinAllComponents(
  layout: GenerationLayoutConfiguration,
  rectangles: readonly BulkPinnableRectangle[],
  options: { pinnedAt?: string } = {},
): LayoutPatch | null {
  const customIds = new Set((layout.customComponents ?? []).map(custom => custom.id));
  const existing = layout.manualPlacements ?? [];
  const alreadyPinned = new Set(existing.map(placement => placement.componentId));
  const pinnedAt = options.pinnedAt ?? new Date().toISOString();
  const added = rectangles
    .filter(rectangle => (
      rectangle.kind !== 'access-road'
      && !customIds.has(rectangle.id)
      && !alreadyPinned.has(rectangle.id)
    ))
    .map(rectangle => ({
      componentId: rectangle.id,
      xM: rectangle.xM,
      yM: rectangle.yM,
      rotationDeg: rectangle.rotationDeg ?? 0,
      pinnedAt,
    }));
  if (added.length === 0) return null;
  return {
    schemaVersion: 2,
    manualPlacements: [...existing, ...added],
  };
}

/**
 * Hand the whole layout back to the packer by clearing every pin — the "auto"
 * end of the placement axis. Destructive to hand-placement intent, so it always
 * routes through `commitLayoutChange` (undoable). Returns null when no pins
 * exist.
 */
export function unpinAllComponents(
  layout: GenerationLayoutConfiguration,
): LayoutPatch | null {
  if ((layout.manualPlacements ?? []).length === 0) return null;
  return { schemaVersion: 2, manualPlacements: [] };
}

export function addCustomComponent(
  layout: GenerationLayoutConfiguration,
  custom: GenerationCustomComponent,
): LayoutPatch {
  return {
    schemaVersion: 2,
    customComponents: [...(layout.customComponents ?? []), custom],
  };
}

export function patchCustomComponent(
  layout: GenerationLayoutConfiguration,
  customId: string,
  patch: Partial<GenerationCustomComponent>,
): LayoutPatch {
  return {
    schemaVersion: 2,
    customComponents: (layout.customComponents ?? []).map(current => (
      current.id === customId ? { ...current, ...patch, id: current.id } : current
    )),
  };
}

export function removeCustomComponent(
  layout: GenerationLayoutConfiguration,
  customId: string,
): LayoutPatch {
  return {
    schemaVersion: 2,
    customComponents: (layout.customComponents ?? [])
      .filter(current => current.id !== customId),
  };
}

/**
 * Commit a whole-shape drag as a frozen-input patch, or null when the drag
 * is inside the jitter dead-band.
 *
 * The dragged centroid is compared against the mean of the SAME closed ring
 * before the drag, so the duplicated closing vertex biases both means
 * identically and cancels in the delta. Packed components auto-pin at the
 * drop position (rotation preserved); rect customs shift their world
 * position; polygon/polyline customs translate their whole path.
 */
export function componentDragPatch(
  layout: GenerationLayoutConfiguration,
  component: GenerationLayoutComponentPolygon,
  anchor: { lat: number; lng: number },
  draggedCentroid: { lat: number; lng: number },
  pinnedAt?: string,
): LayoutPatch | null {
  if (component.coordinates.length === 0) return null;
  const originalMean = component.coordinates.reduce(
    (sum, point) => ({ lat: sum.lat + point.lat, lng: sum.lng + point.lng }),
    { lat: 0, lng: 0 },
  );
  const meanPoint = {
    lat: originalMean.lat / component.coordinates.length,
    lng: originalMean.lng / component.coordinates.length,
  };
  const originalLocal = localAxesForPoint(meanPoint, anchor, layout.rotationDeg);
  const draggedLocal = localAxesForPoint(draggedCentroid, anchor, layout.rotationDeg);
  const dxM = draggedLocal.xM - originalLocal.xM;
  const dyM = draggedLocal.yM - originalLocal.yM;
  if (Math.sqrt(dxM * dxM + dyM * dyM) < DRAG_DEAD_BAND_M) return null;

  const custom = resolveCustomComponent(layout, component.rectangle.id);
  if (custom) {
    const dLat = draggedCentroid.lat - meanPoint.lat;
    const dLng = draggedCentroid.lng - meanPoint.lng;
    if (custom.shape.type === 'rect') {
      if (!custom.position) return null;
      return patchCustomComponent(layout, custom.id, {
        position: { lat: custom.position.lat + dLat, lng: custom.position.lng + dLng },
      });
    }
    return patchCustomComponent(layout, custom.id, {
      shape: {
        ...custom.shape,
        path: custom.shape.path.map(point => ({ lat: point.lat + dLat, lng: point.lng + dLng })),
      },
    });
  }
  return upsertManualPlacement(layout, {
    componentId: component.rectangle.id,
    xM: component.rectangle.xM + dxM,
    yM: component.rectangle.yM + dyM,
    rotationDeg: component.rectangle.rotationDeg ?? 0,
    ...(pinnedAt ? { pinnedAt } : {}),
  });
}

/**
 * Map a component rectangle id back to its source custom component.
 * Rectangle ids are `custom-<id>`, with a `-<n>` suffix when a buffered road
 * centerline split into multiple corridor polygons.
 */
export function resolveCustomComponent(
  layout: GenerationLayoutConfiguration,
  rectangleId: string,
): GenerationCustomComponent | null {
  if (!rectangleId.startsWith('custom-')) return null;
  const customs = layout.customComponents ?? [];
  const stripped = rectangleId.slice('custom-'.length);
  const exact = customs.find(current => current.id === stripped);
  if (exact) return exact;
  const withoutSuffix = stripped.replace(/-\d+$/, '');
  return customs.find(current => current.id === withoutSuffix) ?? null;
}

// ---------------------------------------------------------------------------
// Manual multi-select tools (align / distribute / nudge).
//
// All operate in the rotated, parcel-centred LOCAL metre frame — the same
// frame pins live in — so a mixed selection of packed/pinned blocks and
// world-frame custom objects moves coherently. Each per-component delta is
// applied through the SAME write paths as a drag: pinned blocks auto-pin at
// their new position; rect customs shift world position; polyline/polygon
// customs translate their whole path. Geometry never scales or rotates here.
// ---------------------------------------------------------------------------

/** Which edge/centre a selection aligns to. */
export type StudioAlignEdge =
  | 'left'
  | 'hcenter'
  | 'right'
  | 'top'
  | 'vmiddle'
  | 'bottom';

/** Axis along which a selection is evenly distributed. */
export type StudioDistributeAxis = 'horizontal' | 'vertical';

interface LocalBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  centerX: number;
  centerY: number;
}

/**
 * Axis-aligned bounding box of a component in the local frame, derived from
 * its resolved world ring (so rotation is captured exactly). Returns null for
 * degenerate/non-finite geometry so callers can skip it rather than move it to
 * a bogus position.
 */
function localComponentBox(
  component: GenerationLayoutComponentPolygon,
  anchor: { lat: number; lng: number },
  rotationDeg: number,
): LocalBox | null {
  if (component.coordinates.length === 0) return null;
  const locals = component.coordinates.map(point => localAxesForPoint(point, anchor, rotationDeg));
  const xs = locals.map(point => point.xM);
  const ys = locals.map(point => point.yM);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  if (![minX, maxX, minY, maxY].every(Number.isFinite)) return null;
  return { minX, maxX, minY, maxY, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2 };
}

/**
 * Return `layout` with `component` translated by (dxM, dyM) in the local frame.
 * Pins update xM/yM (auto-pinning at the new spot, rotation preserved); customs
 * shift by the equivalent world delta. Sub-epsilon moves return `layout` as-is.
 */
function applyLocalTranslation(
  layout: GenerationLayoutConfiguration,
  component: GenerationLayoutComponentPolygon,
  dxM: number,
  dyM: number,
  anchor: { lat: number; lng: number },
): GenerationLayoutConfiguration {
  if (Math.abs(dxM) < TRANSLATION_EPSILON_M && Math.abs(dyM) < TRANSLATION_EPSILON_M) {
    return layout;
  }
  const rotationDeg = layout.rotationDeg;
  const custom = resolveCustomComponent(layout, component.rectangle.id);
  if (custom) {
    // Convert the local delta to a world delta at the component's location.
    const base = latLngForLocalAxes({ xM: component.rectangle.xM, yM: component.rectangle.yM }, anchor, rotationDeg);
    const moved = latLngForLocalAxes(
      { xM: component.rectangle.xM + dxM, yM: component.rectangle.yM + dyM },
      anchor,
      rotationDeg,
    );
    const dLat = moved.lat - base.lat;
    const dLng = moved.lng - base.lng;
    if (custom.shape.type === 'rect') {
      if (!custom.position) return layout;
      return {
        ...layout,
        ...patchCustomComponent(layout, custom.id, {
          position: { lat: custom.position.lat + dLat, lng: custom.position.lng + dLng },
        }),
      };
    }
    return {
      ...layout,
      ...patchCustomComponent(layout, custom.id, {
        shape: {
          ...custom.shape,
          path: custom.shape.path.map(point => ({ lat: point.lat + dLat, lng: point.lng + dLng })),
        },
      }),
    };
  }
  return {
    ...layout,
    ...upsertManualPlacement(layout, {
      componentId: component.rectangle.id,
      xM: component.rectangle.xM + dxM,
      yM: component.rectangle.yM + dyM,
      rotationDeg: component.rectangle.rotationDeg ?? 0,
    }),
  };
}

/** Emit only the collections that actually changed (avoids replay churn). */
function diffLayoutPatch(
  base: GenerationLayoutConfiguration,
  next: GenerationLayoutConfiguration,
): LayoutPatch | null {
  const patch: LayoutPatch = { schemaVersion: 2 };
  let changed = false;
  if (JSON.stringify(base.manualPlacements ?? null) !== JSON.stringify(next.manualPlacements ?? null)) {
    patch.manualPlacements = next.manualPlacements ?? [];
    changed = true;
  }
  if (JSON.stringify(base.customComponents ?? null) !== JSON.stringify(next.customComponents ?? null)) {
    patch.customComponents = next.customComponents ?? [];
    changed = true;
  }
  return changed ? patch : null;
}

/**
 * Translate every selected component by the same local-frame delta. Returns
 * null when nothing moved (e.g. a zero step or an empty selection).
 */
export function nudgeComponents(
  layout: GenerationLayoutConfiguration,
  components: ReadonlyArray<GenerationLayoutComponentPolygon>,
  anchor: { lat: number; lng: number },
  dxM: number,
  dyM: number,
): LayoutPatch | null {
  let next = layout;
  for (const component of components) {
    next = applyLocalTranslation(next, component, dxM, dyM, anchor);
  }
  return diffLayoutPatch(layout, next);
}

/**
 * Align the selection to its own bounding box: edge alignments snap each
 * component's matching edge; centre alignments snap each centre to the
 * selection centre. Needs ≥2 measurable components. Returns null if nothing
 * moves. Deltas are computed from the ORIGINAL boxes, then applied together.
 */
export function alignComponents(
  layout: GenerationLayoutConfiguration,
  components: ReadonlyArray<GenerationLayoutComponentPolygon>,
  anchor: { lat: number; lng: number },
  edge: StudioAlignEdge,
): LayoutPatch | null {
  const boxes = components
    .map(component => ({ component, box: localComponentBox(component, anchor, layout.rotationDeg) }))
    .filter((entry): entry is { component: GenerationLayoutComponentPolygon; box: LocalBox } => entry.box !== null);
  if (boxes.length < 2) return null;

  const minX = Math.min(...boxes.map(entry => entry.box.minX));
  const maxX = Math.max(...boxes.map(entry => entry.box.maxX));
  const minY = Math.min(...boxes.map(entry => entry.box.minY));
  const maxY = Math.max(...boxes.map(entry => entry.box.maxY));
  const selectionCenterX = (minX + maxX) / 2;
  const selectionCenterY = (minY + maxY) / 2;

  let next = layout;
  for (const { component, box } of boxes) {
    let dxM = 0;
    let dyM = 0;
    switch (edge) {
      case 'left': dxM = minX - box.minX; break;
      case 'right': dxM = maxX - box.maxX; break;
      case 'hcenter': dxM = selectionCenterX - box.centerX; break;
      case 'top': dyM = maxY - box.maxY; break;
      case 'bottom': dyM = minY - box.minY; break;
      case 'vmiddle': dyM = selectionCenterY - box.centerY; break;
    }
    next = applyLocalTranslation(next, component, dxM, dyM, anchor);
  }
  return diffLayoutPatch(layout, next);
}

/**
 * Evenly space the selection so the gaps between successive bounding boxes are
 * equal along the chosen axis. The two extreme components stay put; interior
 * components shift. Needs ≥3 measurable components. Returns null if nothing
 * moves or the extremes overlap (no room to distribute).
 */
export function distributeComponents(
  layout: GenerationLayoutConfiguration,
  components: ReadonlyArray<GenerationLayoutComponentPolygon>,
  anchor: { lat: number; lng: number },
  axis: StudioDistributeAxis,
): LayoutPatch | null {
  const boxes = components
    .map(component => ({ component, box: localComponentBox(component, anchor, layout.rotationDeg) }))
    .filter((entry): entry is { component: GenerationLayoutComponentPolygon; box: LocalBox } => entry.box !== null);
  if (boxes.length < 3) return null;

  const horizontal = axis === 'horizontal';
  const lead = (box: LocalBox): number => (horizontal ? box.minX : box.minY);
  const trail = (box: LocalBox): number => (horizontal ? box.maxX : box.maxY);
  const size = (box: LocalBox): number => (horizontal ? box.maxX - box.minX : box.maxY - box.minY);

  const sorted = [...boxes].sort((a, b) => lead(a.box) - lead(b.box));
  const spanStart = lead(sorted[0]!.box);
  const spanEnd = trail(sorted[sorted.length - 1]!.box);
  const totalSize = sorted.reduce((sum, entry) => sum + size(entry.box), 0);
  const freeSpace = (spanEnd - spanStart) - totalSize;
  const gap = freeSpace / (sorted.length - 1);
  if (!Number.isFinite(gap)) return null;

  let cursor = spanStart;
  let next = layout;
  for (let index = 0; index < sorted.length; index += 1) {
    const { component, box } = sorted[index]!;
    const targetLead = index === 0
      ? lead(box)
      : index === sorted.length - 1
        ? spanEnd - size(box)
        : cursor;
    const delta = targetLead - lead(box);
    if (horizontal) next = applyLocalTranslation(next, component, delta, 0, anchor);
    else next = applyLocalTranslation(next, component, 0, delta, anchor);
    cursor = targetLead + size(box) + gap;
  }
  return diffLayoutPatch(layout, next);
}

/**
 * Assist › Pack Selection: arrange the selected components into a tidy uniform
 * grid, top-left-anchored on the selection's own bounding box, with a clear gap
 * between cells. Cell size is the largest selected component so nothing
 * overlaps. Pure translation only — components keep their size and rotation and
 * auto-pin at their grid cell. Returns null for an empty selection.
 */
export function packSelectionPatch(
  layout: GenerationLayoutConfiguration,
  components: ReadonlyArray<GenerationLayoutComponentPolygon>,
  anchor: { lat: number; lng: number },
  options: { spacingM: number; columns?: number } = { spacingM: 20 },
): LayoutPatch | null {
  const boxes = components
    .map(component => ({ component, box: localComponentBox(component, anchor, layout.rotationDeg) }))
    .filter((entry): entry is { component: GenerationLayoutComponentPolygon; box: LocalBox } => entry.box !== null);
  if (boxes.length === 0) return null;

  const spacing = Number.isFinite(options.spacingM) && options.spacingM > 0 ? options.spacingM : 20;
  const cellW = Math.max(...boxes.map(entry => entry.box.maxX - entry.box.minX)) + spacing;
  const cellH = Math.max(...boxes.map(entry => entry.box.maxY - entry.box.minY)) + spacing;
  const columns = Math.max(1, Math.round(options.columns ?? Math.ceil(Math.sqrt(boxes.length))));
  // Anchor the grid on the selection's current top-left so it stays in place.
  const originX = Math.min(...boxes.map(entry => entry.box.minX));
  const originY = Math.max(...boxes.map(entry => entry.box.maxY));

  let next = layout;
  boxes.forEach((entry, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    const cellCenterX = originX + col * cellW + (cellW - spacing) / 2;
    const cellCenterY = originY - row * cellH - (cellH - spacing) / 2;
    next = applyLocalTranslation(next, entry.component, cellCenterX - entry.box.centerX, cellCenterY - entry.box.centerY, anchor);
  });
  return diffLayoutPatch(layout, next);
}

// ---------------------------------------------------------------------------
// Rotate / duplicate / delete / move-to-Unplaced.
//
// Eligibility (required singletons and generation blocks may not be freely
// duplicated/deleted — those route through the fleet-count transaction) is the
// caller's responsibility; these builders only touch the ids they are given.
// Custom deletion/duplication is pure here; unplacing a pin just drops its
// manual placement so the packer reclaims it.
// ---------------------------------------------------------------------------

/** How a rotate request maps to an angle: absolute bearing or relative step. */
export interface StudioRotateSpec {
  mode: 'set' | 'by';
  deg: number;
}

/** Return a custom id derived from `base` that is not already in `taken`. */
function uniqueCustomId(base: string, taken: Set<string>): string {
  let candidate = `${base}-copy`;
  let suffix = 2;
  while (taken.has(candidate)) {
    candidate = `${base}-copy-${suffix}`;
    suffix += 1;
  }
  return candidate;
}

/** Rotate a world path about its own local-frame centroid by `deg` (CCW). */
function rotatePathAboutCentroid(
  path: ReadonlyArray<{ lat: number; lng: number }>,
  deg: number,
  anchor: { lat: number; lng: number },
  rotationDeg: number,
): Array<{ lat: number; lng: number }> {
  const locals = path.map(point => localAxesForPoint(point, anchor, rotationDeg));
  const cx = locals.reduce((sum, point) => sum + point.xM, 0) / locals.length;
  const cy = locals.reduce((sum, point) => sum + point.yM, 0) / locals.length;
  const rad = deg * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return locals.map(({ xM, yM }) => {
    const dx = xM - cx;
    const dy = yM - cy;
    return latLngForLocalAxes(
      { xM: cx + dx * cos - dy * sin, yM: cy + dx * sin + dy * cos },
      anchor,
      rotationDeg,
    );
  });
}

function applyRotation(
  layout: GenerationLayoutConfiguration,
  component: GenerationLayoutComponentPolygon,
  anchor: { lat: number; lng: number },
  spec: StudioRotateSpec,
): GenerationLayoutConfiguration {
  const custom = resolveCustomComponent(layout, component.rectangle.id);
  if (custom) {
    if (custom.shape.type === 'rect') {
      const nextRotation = spec.mode === 'set' ? spec.deg : (custom.rotationDeg ?? 0) + spec.deg;
      return { ...layout, ...patchCustomComponent(layout, custom.id, { rotationDeg: nextRotation }) };
    }
    // Arbitrary paths have no absolute orientation, so only relative steps rotate.
    if (spec.mode === 'set' || spec.deg === 0) return layout;
    return {
      ...layout,
      ...patchCustomComponent(layout, custom.id, {
        shape: {
          ...custom.shape,
          path: rotatePathAboutCentroid(custom.shape.path, spec.deg, anchor, layout.rotationDeg),
        },
      }),
    };
  }
  const current = component.rectangle.rotationDeg ?? 0;
  const nextRotation = spec.mode === 'set' ? spec.deg : current + spec.deg;
  return {
    ...layout,
    ...upsertManualPlacement(layout, {
      componentId: component.rectangle.id,
      xM: component.rectangle.xM,
      yM: component.rectangle.yM,
      rotationDeg: nextRotation,
    }),
  };
}

/**
 * Rotate each selected component about its OWN centre. Pins and rect customs
 * accept absolute ('set') or relative ('by') angles; free paths accept 'by'
 * only. Returns null when nothing changes.
 */
export function rotateComponents(
  layout: GenerationLayoutConfiguration,
  components: ReadonlyArray<GenerationLayoutComponentPolygon>,
  anchor: { lat: number; lng: number },
  spec: StudioRotateSpec,
): LayoutPatch | null {
  let next = layout;
  for (const component of components) {
    next = applyRotation(next, component, anchor, spec);
  }
  return diffLayoutPatch(layout, next);
}

/** Drop the manual placements for `componentIds`, returning them to the packer. */
export function moveComponentsToUnplaced(
  layout: GenerationLayoutConfiguration,
  componentIds: ReadonlyArray<string>,
): LayoutPatch | null {
  const ids = new Set(componentIds);
  const existing = layout.manualPlacements ?? [];
  const next = existing.filter(placement => !ids.has(placement.componentId));
  if (next.length === existing.length) return null;
  return { schemaVersion: 2, manualPlacements: next };
}

/** Delete the custom components behind the given rectangle ids (pins ignored). */
export function deleteCustomComponents(
  layout: GenerationLayoutConfiguration,
  rectangleIds: ReadonlyArray<string>,
): LayoutPatch | null {
  const customIds = new Set<string>();
  for (const rectangleId of rectangleIds) {
    const custom = resolveCustomComponent(layout, rectangleId);
    if (custom) customIds.add(custom.id);
  }
  if (customIds.size === 0) return null;
  const existing = layout.customComponents ?? [];
  const next = existing.filter(custom => !customIds.has(custom.id));
  if (next.length === existing.length) return null;
  return { schemaVersion: 2, customComponents: next };
}

/**
 * Assist › Connect with roads: draw a planning-grade centerline from one
 * entrance to each target component centre. These are straight editable
 * centerlines with a corridor width — NOT civil-grade alignments (no turning
 * radii, grading or junction design). Connectors carry a stable id per target
 * so re-running replaces the prior connector rather than duplicating it, and
 * they never touch manual/imported roads.
 */
export function connectWithRoadsPatch(
  layout: GenerationLayoutConfiguration,
  entrance: { lat: number; lng: number },
  targets: ReadonlyArray<{ id: string; center: { lat: number; lng: number } }>,
  widthM: number,
): LayoutPatch | null {
  if (targets.length === 0 || !Number.isFinite(widthM) || widthM <= 0) return null;
  const connectorId = (targetId: string): string => `connector-${targetId}`;
  const incomingIds = new Set(targets.map(target => connectorId(target.id)));
  const existing = layout.customComponents ?? [];
  const kept = existing.filter(component => !incomingIds.has(component.id));
  const roads: GenerationCustomComponent[] = targets.map(target => ({
    id: connectorId(target.id),
    kind: 'road',
    label: `Access to ${target.id}`,
    shape: {
      type: 'polyline',
      path: [{ ...entrance }, { ...target.center }],
      widthM,
    },
    lifecycle: 'permanent',
    source: 'drawn',
  }));
  return { schemaVersion: 2, customComponents: [...kept, ...roads] };
}

/**
 * Duplicate the custom components behind the given rectangle ids, offsetting
 * each copy by `offsetM` east+north so it is visibly distinct. New ids are
 * derived from the source id and de-duplicated. Pins are ignored.
 */
export function duplicateCustomComponents(
  layout: GenerationLayoutConfiguration,
  rectangleIds: ReadonlyArray<string>,
  anchor: { lat: number; lng: number },
  offsetM = 15,
): LayoutPatch | null {
  const existing = layout.customComponents ?? [];
  const taken = new Set(existing.map(custom => custom.id));
  const origin = latLngForLocalAxes({ xM: 0, yM: 0 }, anchor, layout.rotationDeg);
  const shifted = latLngForLocalAxes({ xM: offsetM, yM: offsetM }, anchor, layout.rotationDeg);
  const dLat = shifted.lat - origin.lat;
  const dLng = shifted.lng - origin.lng;

  const copies: GenerationCustomComponent[] = [];
  const seen = new Set<string>();
  for (const rectangleId of rectangleIds) {
    const custom = resolveCustomComponent(layout, rectangleId);
    if (!custom || seen.has(custom.id)) continue;
    seen.add(custom.id);
    const newId = uniqueCustomId(custom.id, taken);
    taken.add(newId);
    if (custom.shape.type === 'rect') {
      copies.push({
        ...custom,
        id: newId,
        ...(custom.position ? { position: { lat: custom.position.lat + dLat, lng: custom.position.lng + dLng } } : {}),
      });
    } else {
      copies.push({
        ...custom,
        id: newId,
        shape: {
          ...custom.shape,
          path: custom.shape.path.map(point => ({ lat: point.lat + dLat, lng: point.lng + dLng })),
        },
      });
    }
  }
  if (copies.length === 0) return null;
  return { schemaVersion: 2, customComponents: [...existing, ...copies] };
}
